import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ClientHome } from '../../types/business';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { analytics } from '../../services/analytics';

interface ClientHomesTableProps {
  homes: ClientHome[];
  isLoading: boolean;
  clientName: string;
  clientEmail?: string;
}

const ClientHomesTable = ({ homes, isLoading, clientName, clientEmail }: ClientHomesTableProps) => {
  const navigate = useNavigate();
  
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Loading properties...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ borderRadius: 2 }}>
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon />
          {clientName}'s Properties ({homes.length})
        </Typography>
      </Box>

      {homes.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No properties found for this client.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Property Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {homes.map((home) => (
                <TableRow key={home.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {home.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {home.address ? (
                      <Typography
                        variant="body2"
                        color="primary.main"
                        component="a"
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(home.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {home.address}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No address specified
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={home.analysisProgress}
                        sx={{ width: 80, height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {home.analysisProgress}%
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {home.completedTasks}/{home.tasksCount} tasks completed
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(home.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(home.lastUpdated)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Property Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Log agent_client_home_walkthrough_open analytics event
                            if (home.id) {
                              analytics.logAgentClientHomeWalkthroughOpen('row_tap', home.id);
                            }
                            navigate(`/home/${home.id}`);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Analytics">
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Navigate to analytics view
                            navigate(`/business/agent/client/${encodeURIComponent(clientEmail || '')}/analytics/${home.id}`);
                          }}
                        >
                          <AnalyticsIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default ClientHomesTable;